import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Radio } from 'lucide-react';

const ModuleVisualization = () => {
  const [activeShot, setActiveShot] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [hoveredSensor, setHoveredSensor] = useState(null);

  const shots = [
    { id: 0, name: 'Product Reveal', description: 'The ClearWay assistive module' },
    { id: 1, name: 'Mounting System', description: 'Secure collar attachment' },
    { id: 2, name: 'Sensor Layout', description: 'IR and ultrasonic placement' },
    { id: 3, name: 'Ground Detection', description: 'Downward IR scanning' },
    { id: 4, name: 'Forward Detection', description: 'Ultrasonic obstacle sensing' },
    { id: 5, name: 'Combined Intelligence', description: 'Dual-zone awareness' },
  ];

  // Auto-advance shots
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setActiveShot((prev) => (prev + 1) % shots.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, shots.length]);

  const handleShotClick = (index) => {
    setActiveShot(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <div className="relative">
      {/* Main Visualization Area */}
      <div className="relative aspect-square max-w-lg mx-auto">
        {/* Background glow */}
        <div className="absolute inset-0 bg-gradient-radial from-emerald-500/5 via-transparent to-transparent" />
        
        {/* The Walking Stick and Module Assembly */}
        <svg
          viewBox="0 0 400 500"
          className="w-full h-full"
          style={{ filter: 'drop-shadow(0 0 30px rgba(16, 185, 129, 0.15))' }}
        >
          <defs>
            {/* Gradients */}
            <linearGradient id="stickGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4a4a4a" />
              <stop offset="50%" stopColor="#2a2a2a" />
              <stop offset="100%" stopColor="#1a1a1a" />
            </linearGradient>
            
            <linearGradient id="moduleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#3a3a3a" />
              <stop offset="50%" stopColor="#2a2a2a" />
              <stop offset="100%" stopColor="#1a1a1a" />
            </linearGradient>

            <linearGradient id="irBeamGradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(168, 85, 247, 0.6)" />
              <stop offset="100%" stopColor="rgba(168, 85, 247, 0)" />
            </linearGradient>

            <linearGradient id="ultrasonicGradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.5)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
            </linearGradient>

            <filter id="glow">
              <feGaussianBlur stdDeviation="3" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="innerShadow">
              <feOffset dx="0" dy="2" />
              <feGaussianBlur stdDeviation="2" result="offset-blur" />
              <feComposite operator="out" in="SourceGraphic" in2="offset-blur" result="inverse" />
              <feFlood floodColor="black" floodOpacity="0.3" result="color" />
              <feComposite operator="in" in="color" in2="inverse" result="shadow" />
              <feComposite operator="over" in="shadow" in2="SourceGraphic" />
            </filter>
          </defs>

          {/* Walking Stick Shaft */}
          <motion.g
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Main stick body */}
            <rect
              x="188"
              y="80"
              width="24"
              height="380"
              rx="12"
              fill="url(#stickGradient)"
              className="drop-shadow-lg"
            />
            {/* Stick highlight */}
            <rect
              x="192"
              y="80"
              width="4"
              height="380"
              rx="2"
              fill="#5a5a5a"
              opacity="0.4"
            />
            {/* Rubber tip */}
            <ellipse cx="200" cy="460" rx="16" ry="8" fill="#1a1a1a" />
          </motion.g>

          {/* Module Housing */}
          <motion.g
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ 
              opacity: 1, 
              scale: 1,
              y: activeShot === 1 ? [0, -10, 0] : 0
            }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            {/* Module body - rounded rectangle */}
            <rect
              x="160"
              y="120"
              width="80"
              height="100"
              rx="12"
              fill="url(#moduleGradient)"
              filter="url(#innerShadow)"
              stroke="#4a4a4a"
              strokeWidth="1"
            />
            
            {/* Module top cap */}
            <ellipse
              cx="200"
              cy="122"
              rx="40"
              ry="8"
              fill="#3a3a3a"
            />

            {/* Cylindrical Mounting Collar (Bottom) */}
            <motion.g
              animate={{
                opacity: activeShot >= 1 ? 1 : 0.7,
                scale: activeShot === 1 ? [1, 1.02, 1] : 1
              }}
              transition={{ duration: 0.5, repeat: activeShot === 1 ? Infinity : 0, repeatType: 'reverse' }}
            >
              <ellipse
                cx="200"
                cy="220"
                rx="35"
                ry="10"
                fill="#2a2a2a"
                stroke={activeShot === 1 ? '#10b981' : '#3a3a3a'}
                strokeWidth={activeShot === 1 ? 2 : 1}
              />
              <rect
                x="165"
                y="210"
                width="70"
                height="25"
                fill="#2a2a2a"
                stroke={activeShot === 1 ? '#10b981' : '#3a3a3a'}
                strokeWidth={activeShot === 1 ? 2 : 1}
              />
              <ellipse
                cx="200"
                cy="235"
                rx="35"
                ry="10"
                fill="#2a2a2a"
                stroke={activeShot === 1 ? '#10b981' : '#3a3a3a'}
                strokeWidth={activeShot === 1 ? 2 : 1}
              />
              {/* Inner hole for stick */}
              <ellipse
                cx="200"
                cy="235"
                rx="14"
                ry="5"
                fill="#1a1a1a"
              />
            </motion.g>

            {/* Front Face - Ultrasonic Sensor Holes */}
            <motion.g
              onMouseEnter={() => setHoveredSensor('ultrasonic')}
              onMouseLeave={() => setHoveredSensor(null)}
              className="cursor-pointer"
              animate={{
                scale: hoveredSensor === 'ultrasonic' || activeShot === 4 || activeShot === 2 ? 1.05 : 1
              }}
            >
              {/* Left ultrasonic hole */}
              <circle
                cx="180"
                cy="145"
                r="10"
                fill="#1a1a1a"
                stroke={activeShot === 4 || activeShot === 2 ? '#3b82f6' : '#3a3a3a'}
                strokeWidth={activeShot === 4 || activeShot === 2 ? 2 : 1}
              />
              <circle
                cx="180"
                cy="145"
                r="6"
                fill="#0f0f0f"
              />
              {/* Blue indicator LED */}
              <motion.circle
                cx="180"
                cy="145"
                r="3"
                fill="#3b82f6"
                animate={{ opacity: activeShot >= 4 ? [0.3, 1, 0.3] : 0.3 }}
                transition={{ duration: 1.5, repeat: Infinity }}
                filter="url(#glow)"
              />

              {/* Right ultrasonic hole */}
              <circle
                cx="220"
                cy="145"
                r="10"
                fill="#1a1a1a"
                stroke={activeShot === 4 || activeShot === 2 ? '#3b82f6' : '#3a3a3a'}
                strokeWidth={activeShot === 4 || activeShot === 2 ? 2 : 1}
              />
              <circle
                cx="220"
                cy="145"
                r="6"
                fill="#0f0f0f"
              />
              <motion.circle
                cx="220"
                cy="145"
                r="3"
                fill="#3b82f6"
                animate={{ opacity: activeShot >= 4 ? [0.3, 1, 0.3] : 0.3 }}
                transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
                filter="url(#glow)"
              />
            </motion.g>

            {/* Bottom Face - IR Sensor Rectangular Cutout */}
            <motion.g
              onMouseEnter={() => setHoveredSensor('ir')}
              onMouseLeave={() => setHoveredSensor(null)}
              className="cursor-pointer"
              animate={{
                scale: hoveredSensor === 'ir' || activeShot === 3 || activeShot === 2 ? 1.05 : 1
              }}
            >
              <rect
                x="175"
                y="195"
                width="50"
                height="15"
                rx="3"
                fill="#1a1a1a"
                stroke={activeShot === 3 || activeShot === 2 ? '#a855f7' : '#3a3a3a'}
                strokeWidth={activeShot === 3 || activeShot === 2 ? 2 : 1}
              />
              {/* IR LED indicator */}
              <motion.rect
                x="185"
                y="199"
                width="30"
                height="6"
                rx="2"
                fill="#a855f7"
                animate={{ opacity: activeShot >= 3 ? [0.2, 0.8, 0.2] : 0.2 }}
                transition={{ duration: 2, repeat: Infinity }}
                filter="url(#glow)"
              />
            </motion.g>

            {/* Status LED on module front */}
            <motion.circle
              cx="200"
              cy="175"
              r="4"
              fill="#10b981"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
              filter="url(#glow)"
            />
          </motion.g>

          {/* IR Detection Beam (Downward) */}
          <AnimatePresence>
            {(activeShot === 3 || activeShot === 5) && (
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* IR beam cone */}
                <motion.polygon
                  points="200,215 160,400 240,400"
                  fill="url(#irBeamGradient)"
                  animate={{ opacity: [0.3, 0.6, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
                
                {/* Ground scan ellipse */}
                <motion.ellipse
                  cx="200"
                  cy="400"
                  rx="45"
                  ry="12"
                  fill="rgba(168, 85, 247, 0.3)"
                  stroke="rgba(168, 85, 247, 0.6)"
                  strokeWidth="1"
                  animate={{ 
                    rx: [45, 50, 45],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                {/* Detected hazard (pothole) */}
                {activeShot === 3 && (
                  <motion.g
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <ellipse
                      cx="215"
                      cy="405"
                      rx="20"
                      ry="6"
                      fill="#1a1a1a"
                      stroke="#ef4444"
                      strokeWidth="2"
                      strokeDasharray="4 2"
                    />
                    <motion.text
                      x="250"
                      y="410"
                      fill="#ef4444"
                      fontSize="10"
                      fontWeight="500"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      HAZARD
                    </motion.text>
                  </motion.g>
                )}
              </motion.g>
            )}
          </AnimatePresence>

          {/* Ultrasonic Detection Cones (Forward) */}
          <AnimatePresence>
            {(activeShot === 4 || activeShot === 5) && (
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Left ultrasonic cone */}
                <motion.path
                  d="M 180 145 Q 80 100 60 60"
                  fill="none"
                  stroke="rgba(59, 130, 246, 0.5)"
                  strokeWidth="30"
                  strokeLinecap="round"
                  animate={{ 
                    strokeOpacity: [0.2, 0.5, 0.2],
                    pathLength: [0.5, 1, 0.5]
                  }}
                  transition={{ duration: 2.5, repeat: Infinity }}
                />
                
                {/* Right ultrasonic cone */}
                <motion.path
                  d="M 220 145 Q 320 100 340 60"
                  fill="none"
                  stroke="rgba(59, 130, 246, 0.5)"
                  strokeWidth="30"
                  strokeLinecap="round"
                  animate={{ 
                    strokeOpacity: [0.2, 0.5, 0.2],
                    pathLength: [0.5, 1, 0.5]
                  }}
                  transition={{ duration: 2.5, repeat: Infinity, delay: 0.3 }}
                />

                {/* Detection arcs */}
                {[1, 2, 3].map((i) => (
                  <motion.ellipse
                    key={i}
                    cx="200"
                    cy="100"
                    rx={60 + i * 30}
                    ry={20 + i * 10}
                    fill="none"
                    stroke="rgba(59, 130, 246, 0.3)"
                    strokeWidth="1"
                    animate={{ 
                      opacity: [0.1, 0.4, 0.1],
                      rx: [60 + i * 30, 70 + i * 30, 60 + i * 30]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.2 }}
                  />
                ))}

                {/* Detected obstacle */}
                {activeShot === 4 && (
                  <motion.g
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 }}
                  >
                    <rect
                      x="80"
                      y="60"
                      width="30"
                      height="50"
                      rx="4"
                      fill="#3a3a3a"
                      stroke="#f59e0b"
                      strokeWidth="2"
                    />
                    <motion.text
                      x="75"
                      y="50"
                      fill="#f59e0b"
                      fontSize="10"
                      fontWeight="500"
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      OBSTACLE
                    </motion.text>
                  </motion.g>
                )}
              </motion.g>
            )}
          </AnimatePresence>

          {/* Sensor Labels */}
          <AnimatePresence>
            {activeShot === 2 && (
              <motion.g
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Ultrasonic label */}
                <line x1="180" y1="145" x2="100" y2="120" stroke="#3b82f6" strokeWidth="1" strokeDasharray="3 3" />
                <rect x="40" y="105" width="65" height="30" rx="4" fill="#1a1a1a" stroke="#3b82f6" strokeWidth="1" />
                <text x="48" y="120" fill="#3b82f6" fontSize="8" fontWeight="500">ULTRASONIC</text>
                <text x="48" y="130" fill="#6b7280" fontSize="7">Forward detect</text>

                {/* IR label */}
                <line x1="200" y1="202" x2="280" y2="250" stroke="#a855f7" strokeWidth="1" strokeDasharray="3 3" />
                <rect x="270" y="235" width="60" height="30" rx="4" fill="#1a1a1a" stroke="#a855f7" strokeWidth="1" />
                <text x="278" y="250" fill="#a855f7" fontSize="8" fontWeight="500">IR SENSOR</text>
                <text x="278" y="260" fill="#6b7280" fontSize="7">Ground scan</text>

                {/* Collar label */}
                <line x1="235" y1="225" x2="300" y2="200" stroke="#10b981" strokeWidth="1" strokeDasharray="3 3" />
                <rect x="295" y="185" width="55" height="30" rx="4" fill="#1a1a1a" stroke="#10b981" strokeWidth="1" />
                <text x="303" y="200" fill="#10b981" fontSize="8" fontWeight="500">COLLAR</text>
                <text x="303" y="210" fill="#6b7280" fontSize="7">Secure mount</text>
              </motion.g>
            )}
          </AnimatePresence>
        </svg>

        {/* Floating Info Cards */}
        <AnimatePresence>
          {hoveredSensor === 'ultrasonic' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute top-4 left-4 p-4 rounded-xl bg-gray-900/95 border border-blue-500/30 backdrop-blur-sm max-w-[200px]"
            >
              <div className="flex items-center gap-2 mb-2">
                <Radio className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">Ultrasonic Sensors</span>
              </div>
              <p className="text-xs text-gray-400">
                Two forward-facing transducers detect obstacles in your walking path using sound waves.
              </p>
            </motion.div>
          )}

          {hoveredSensor === 'ir' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-20 right-4 p-4 rounded-xl bg-gray-900/95 border border-purple-500/30 backdrop-blur-sm max-w-[200px]"
            >
              <div className="flex items-center gap-2 mb-2">
                <Eye className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-medium text-purple-400">IR Ground Sensor</span>
              </div>
              <p className="text-xs text-gray-400">
                Rectangular cutout houses downward-facing IR sensor for detecting potholes, drops, and steps.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Shot Navigation */}
      <div className="mt-8">
        <div className="flex flex-wrap justify-center gap-2">
          {shots.map((shot, index) => (
            <button
              key={shot.id}
              onClick={() => handleShotClick(index)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all ${
                activeShot === index
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40'
                  : 'bg-gray-800/50 text-gray-400 border border-gray-700/50 hover:border-gray-600'
              }`}
            >
              {shot.name}
            </button>
          ))}
        </div>
        
        <motion.div
          key={activeShot}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mt-4"
        >
          <p className="text-gray-400 text-sm">{shots[activeShot].description}</p>
        </motion.div>
      </div>

      {/* Sensor Legend */}
      <div className="flex justify-center gap-6 mt-8">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-xs text-gray-400">Ultrasonic (Forward)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-xs text-gray-400">IR (Ground)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-xs text-gray-400">System Status</span>
        </div>
      </div>
    </div>
  );
};

export default ModuleVisualization;
